// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState } from 'react'
import { ShieldCheck, Loader2, Save, Pencil, AlertCircle, Network, ChevronRight, Copy, Check } from 'lucide-react'
import * as api from '../../api/client'

function CopyBtn({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      title="Copy"
      aria-label="Copy to clipboard"
      onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200) }}
      style={{
        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent',
        border: '1px solid var(--color-border, #d1d5db)', color: copied ? '#15803d' : 'var(--color-text-mute, #6b7280)',
        padding: '3px 7px', fontSize: 11, borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

/**
 * SaaS-admin Network Security tab.
 *
 * Per-pharmacy IP allowlist for inbound vendor ingest feeds. Today the only ingest vendor is
 * PioneerRx, whose Rx Event webhook authenticates with Basic Auth only (no payload signature is
 * possible). Binding a pharmacy's credential to its known static egress IP means a leaked
 * username/password is useless from any other origin — the defense against a cracked webhook
 * credential being used to inject tampered PHI.
 *
 * Platform-admin (SaaS mode) manages this; the pharmacy sees it read-only on its own settings page.
 * The model is vendor-neutral so future ingest vendors slot in as additional rows.
 */
export default function NetworkSecurityTab() {
  const [pharmacies, setPharmacies] = useState<api.PharmacyListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<api.PharmacyListItem | null>(null)

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true); setError(null)
    try {
      const r = await api.listPharmacies()
      setPharmacies(r.items.filter(p => !p.archived_at))
    } catch (e: any) {
      setError(e?.message || 'Failed to load pharmacies')
    } finally { setLoading(false) }
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
          <Network size={18} /> Network Security
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-mute, #6b7280)' }}>
          Restrict each pharmacy's vendor ingest feeds to the pharmacy's own network. A credential bound
          to a static IP can't be used from anywhere else, even if the URL and password are leaked.
        </p>
      </header>

      {error && (
        <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</div>
      )}

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-mute)' }}>
          <Loader2 size={18} className="spin" /> Loading…
        </div>
      ) : selected ? (
        <PharmacySecurityDetail
          pharmacy={selected}
          onBack={() => { setSelected(null); void load() }}
        />
      ) : (
        <div style={listWrap}>
          {pharmacies.length === 0 && (
            <p style={{ padding: 16, fontSize: 13, color: 'var(--color-text-mute)' }}>No pharmacies.</p>
          )}
          {pharmacies.map(p => (
            <button key={p.id} onClick={() => setSelected(p)} style={listRow}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                  {p.city && p.state_code ? `${p.city}, ${p.state_code} · ` : ''}
                  {p.active_creds_created_at ? 'Ingest credential active' : 'No ingest credential'}
                </div>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--color-text-mute)' }} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function PharmacySecurityDetail({ pharmacy, onBack }: { pharmacy: api.PharmacyListItem; onBack: () => void }) {
  const [data, setData] = useState<api.IngestSecurityResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void load() }, [pharmacy.id])
  async function load() {
    setLoading(true); setError(null)
    try { setData(await api.getIngestSecurity(pharmacy.id)) }
    catch (e: any) { setError(e?.message || 'Failed to load') }
    finally { setLoading(false) }
  }

  return (
    <div>
      <button onClick={onBack} style={backBtn}>← All pharmacies</button>
      <h3 style={{ fontSize: 16, margin: '8px 0 12px' }}>{pharmacy.name}</h3>
      {error && <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</div>}
      {loading ? (
        <div style={{ padding: 16, color: 'var(--color-text-mute)' }}><Loader2 size={16} className="spin" /> Loading…</div>
      ) : data ? (
        <>
          {data.vendors.map(v => (
            <VendorAllowlistCard key={v.vendor} pharmacyId={pharmacy.id} vendor={v} onChanged={load} onError={setError} />
          ))}
          <AiConfigCard pharmacyId={pharmacy.id} onError={setError} />
        </>
      ) : null}
    </div>
  )
}

function VendorAllowlistCard({ pharmacyId, vendor, onChanged, onError }: {
  pharmacyId: string
  vendor: api.IngestSecurityVendor
  onChanged: () => void
  onError: (e: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(vendor.ip_allowlist || '')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const restricted = !!vendor.ip_allowlist

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <ShieldCheck size={16} style={{ color: restricted ? '#15803d' : 'var(--color-text-mute)' }} />
        <strong style={{ fontSize: 14 }}>{vendor.display_name}</strong>
        {restricted
          ? <span style={badgeOk}>Restricted</span>
          : <span style={badgeWarn}>Any origin</span>}
      </div>

      {!vendor.has_credential ? (
        <p style={{ margin: '4px 0', fontSize: 13, color: '#92400e' }}>
          No active ingest credential for this pharmacy yet. Generate the credential first; then you can
          set its allowed IPs here.
        </p>
      ) : (
        <>
          {vendor.webhook_url && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8, minWidth: 0 }}>
              <span style={{ fontSize: 12, color: 'var(--color-text-mute)', flexShrink: 0, paddingTop: 3 }}>Webhook:</span>
              <code style={{ ...code, flex: 1 }}>{vendor.webhook_url}</code>
              <CopyBtn value={vendor.webhook_url} />
            </div>
          )}
          {!editing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <code style={code}>{vendor.ip_allowlist || '— no restriction —'}</code>
              <button onClick={() => { setValue(vendor.ip_allowlist || ''); setMsg(null); setEditing(true) }} style={btnSecondary}>
                <Pencil size={12} /> {restricted ? 'Edit' : 'Set IPs'}
              </button>
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder="e.g. 203.0.113.7  (comma-separated for multiple)"
                style={input}
              />
              <p style={{ margin: '6px 0', fontSize: 12, color: 'var(--color-text-mute)' }}>
                Enter the pharmacy's static public egress IP. Leave empty to remove the restriction.
                Only use a <em>static</em> IP — a dynamic one will intermittently block the feed.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true); setMsg(null)
                    try {
                      const r = await api.setIngestSecurityIpAllowlist(pharmacyId, value, vendor.vendor)
                      setMsg(r.message); setEditing(false); onChanged()
                    } catch (e: any) { onError(e?.message || 'Failed to save') }
                    finally { setBusy(false) }
                  }}
                  style={btnPrimary}
                >
                  {busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save
                </button>
                <button disabled={busy} onClick={() => { setEditing(false); setMsg(null) }} style={btnSecondary}>Cancel</button>
              </div>
            </div>
          )}
          {msg && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#15803d' }}>{msg}</p>}
        </>
      )}
    </div>
  )
}

function AiConfigCard({ pharmacyId, onError }: { pharmacyId: string; onError: (e: string) => void }) {
  const [cfg, setCfg] = useState<api.AiConfigResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [keyVal, setKeyVal] = useState('')
  const [limitVal, setLimitVal] = useState('')
  const [editingKey, setEditingKey] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => { void load() }, [pharmacyId])
  async function load() {
    setLoading(true)
    try {
      const c = await api.getAiConfig(pharmacyId)
      setCfg(c); setLimitVal(c.monthly_token_limit != null ? String(c.monthly_token_limit) : '')
    } catch (e: any) { onError(e?.message || 'Failed to load AI config') }
    finally { setLoading(false) }
  }

  async function saveKey() {
    if (!keyVal.trim()) return
    setBusy(true); setMsg(null)
    try {
      const c = await api.setAiConfig(pharmacyId, { apiKey: keyVal.trim() })
      setCfg(c); setKeyVal(''); setEditingKey(false); setMsg('Key saved to Key Vault.')
    } catch (e: any) { onError(e?.message || 'Failed to save key') }
    finally { setBusy(false) }
  }
  async function saveLimit() {
    setBusy(true); setMsg(null)
    try {
      const parsed = limitVal.trim() === '' ? null : Number(limitVal.replace(/[, ]/g, ''))
      if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) { onError('Token limit must be a positive number'); setBusy(false); return }
      const c = await api.setAiConfig(pharmacyId, { monthlyTokenLimit: parsed })
      setCfg(c); setMsg('Token limit updated.')
    } catch (e: any) { onError(e?.message || 'Failed to save limit') }
    finally { setBusy(false) }
  }

  if (loading) return <div style={card}><Loader2 size={14} className="spin" /> Loading AI config…</div>
  const configured = cfg?.key_configured

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <ShieldCheck size={16} style={{ color: configured ? '#15803d' : 'var(--color-text-mute)' }} />
        <strong style={{ fontSize: 14 }}>CareRx AI — Anthropic API key</strong>
        {configured
          ? <span style={badgeOk}>Key set ····{cfg?.key_last4}</span>
          : <span style={badgeWarn}>No key</span>}
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '6px 8px' }}>
        Entering a key does <b>not</b> turn CareRx AI on. PHI egress stays disabled until the Owl↔Anthropic BAA is confirmed.
        The key is stored in Key Vault and never shown again — only its last 4 characters.
      </p>

      {/* Key */}
      {!editingKey ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <code style={code}>{configured ? `Anthropic key ····${cfg?.key_last4}` : '— no key configured —'}</code>
          <button onClick={() => { setKeyVal(''); setMsg(null); setEditingKey(true) }} style={btnSecondary}>
            <Pencil size={12} /> {configured ? 'Replace key' : 'Set key'}
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <input type="password" autoComplete="off" value={keyVal} onChange={e => setKeyVal(e.target.value)}
                 placeholder="sk-ant-…" style={input} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button disabled={busy} onClick={saveKey} style={btnPrimary}>
              {busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save key
            </button>
            <button disabled={busy} onClick={() => { setEditingKey(false); setMsg(null) }} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      {/* Monthly token limit (admin governance) */}
      <div style={{ borderTop: '1px solid var(--color-border, #e5e7eb)', paddingTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Monthly token limit</div>
        <p style={{ margin: '0 0 6px', fontSize: 11, color: 'var(--color-text-mute)' }}>
          Cap on CareRx AI tokens per calendar month. Leave empty for no cap. Over the cap, AI calls return an error until next month.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="text" inputMode="numeric" value={limitVal} onChange={e => setLimitVal(e.target.value)}
                 placeholder="no cap" style={{ ...input, maxWidth: 200 }} />
          <button disabled={busy} onClick={saveLimit} style={btnSecondary}>
            {busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save limit
          </button>
        </div>
      </div>
      {msg && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#15803d' }}>{msg}</p>}
    </div>
  )
}

/* ---- styles ---- */
const listWrap: React.CSSProperties = { border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }
const listRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12,
  padding: '12px 14px', background: 'var(--color-surface, #fff)', border: 'none',
  borderBottom: '1px solid var(--color-border, #f3f4f6)', cursor: 'pointer', textAlign: 'left',
}
const card: React.CSSProperties = {
  border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, padding: 16, marginBottom: 12,
  background: 'var(--color-surface, #fff)',
}
const code: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 12, display: 'block', minWidth: 0, maxWidth: '100%',
  background: 'var(--color-surface-mute, #f3f4f6)',
  padding: '2px 6px', borderRadius: 4, wordBreak: 'break-all', overflowWrap: 'anywhere',
}
const input: React.CSSProperties = {
  width: '100%', maxWidth: 440, padding: '7px 9px', fontSize: 13, fontFamily: 'monospace',
  border: '1px solid var(--color-border, #d1d5db)', borderRadius: 4,
}
const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600,
  color: '#fff', background: 'var(--color-primary, #2563eb)', border: 'none', borderRadius: 4, cursor: 'pointer',
}
const btnSecondary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12,
  color: 'var(--color-text, #374151)', background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border, #d1d5db)', borderRadius: 4, cursor: 'pointer',
}
const backBtn: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-primary, #2563eb)', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
}
const badgeOk: React.CSSProperties = { fontSize: 11, color: '#15803d', background: '#dcfce7', borderRadius: 4, padding: '1px 6px' }
const badgeWarn: React.CSSProperties = { fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '1px 6px' }
const errBox: React.CSSProperties = {
  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 10, fontSize: 13, color: '#991b1b', marginBottom: 12,
}
